/**
 * Request schemas of module 2, Collecte (Functional Specifications section 8): pickup requests,
 * stock movements, chargements with their expéditions, and the actions of the BSD lifecycle.
 */
import { z } from 'zod';
import { BSD_TYPES, QUANTITY_UNITS, TRANSPORT_MODES, localeSchema } from './enums.js';
import { uuidSchema } from './schemas.js';
import { CHECKLIST_ITEMS } from './checklist.js';
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
const reason = z.string().trim().min(10, 'Give a reason of at least 10 characters').max(1000);
/** Tonnes with at most three decimals (BR-09). */
const tonnes = z.number().nonnegative().max(100_000);
export const pickupRequestLineSchema = z.object({
    wasteId: uuidSchema,
    quantity: z.number().positive().max(1_000_000).optional(),
    unit: z.enum(QUANTITY_UNITS).default('KG'),
    conditionnementId: uuidSchema.optional(),
    containerCount: z.number().int().min(1).max(10_000).optional(),
});
/** FR-COL-01: an internal request raised from an origin. */
export const pickupRequestCreateSchema = z.object({
    siteId: uuidSchema,
    originId: uuidSchema.optional(),
    wantedDate: isoDate,
    comment: z.string().trim().max(2000).optional(),
    lines: z.array(pickupRequestLineSchema).min(1).max(50),
});
export const pickupRequestRefuseSchema = z.object({ reason });
/** FR-COL-03: the pickup order e-mailed to the activated prestataire. */
export const pickupOrderSchema = z.object({
    prestataireId: uuidSchema,
    to: z.array(z.string().trim().toLowerCase().email()).min(1).max(10),
    cc: z.array(z.string().trim().toLowerCase().email()).max(10).default([]),
    message: z.string().trim().max(2000).optional(),
});
/** FR-COL-03: the prestataire's confirmation of the order, recorded by the site. */
export const pickupConfirmationSchema = z.object({
    confirmedAt: isoDate,
    reference: z.string().trim().max(120).optional(),
});
/** FR-COL-02: planning creates draft expéditions from the filières and groups them into a chargement. */
export const pickupRequestPlanSchema = z.object({
    plannedDate: isoDate,
    transporterId: uuidSchema.optional(),
});
/** FR-COL-07, FR-COL-09: production entry or inventory adjustment. */
export const stockMovementCreateSchema = z
    .object({
    siteId: uuidSchema,
    wasteId: uuidSchema,
    originId: uuidSchema.optional(),
    conditionnementId: uuidSchema.optional(),
    kind: z.enum(['IN', 'ADJUSTMENT']),
    /** Tonnes; an adjustment may be negative. */
    quantityT: z.number().min(-100_000).max(100_000),
    movementDate: isoDate,
    reason: z.string().trim().max(500).optional(),
    weighingTicket: z.string().trim().max(80).optional(),
})
    .superRefine((m, ctx) => {
    if (m.quantityT === 0) {
        ctx.addIssue({ code: 'custom', path: ['quantityT'], message: 'Quantity cannot be zero' });
    }
    if (m.kind === 'IN' && m.quantityT < 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['quantityT'],
            message: 'A production entry is positive',
        });
    }
    if (m.kind === 'ADJUSTMENT' && !m.reason?.trim()) {
        ctx.addIssue({ code: 'custom', path: ['reason'], message: 'An adjustment needs a reason' });
    }
});
export const packageLineSchema = z.object({
    conditionnementId: uuidSchema,
    count: z.number().int().min(1).max(10_000),
});
export const originSplitSchema = z.object({
    originId: uuidSchema,
    quantityT: tonnes,
});
export const exemptionSchema = z.object({
    rule: z.string().regex(/^BR-\d{2}$/),
    reason,
});
/** One expédition of a chargement (wizard step 1: FR-COL-12 to FR-COL-16). */
export const expeditionInputSchema = z.object({
    wasteId: uuidSchema,
    bsdType: z.enum(BSD_TYPES),
    quantityT: tonnes.default(0),
    quantityEstimated: z.boolean().default(false),
    otherQuantity: z.number().nonnegative().max(1_000_000).optional(),
    otherUnit: z.enum(['M3', 'L', 'UNIT']).optional(),
    packages: z.array(packageLineSchema).max(20).default([]),
    originId: uuidSchema.optional(),
    originSplits: z.array(originSplitSchema).max(50).default([]),
    traderId: uuidSchema.optional(),
    storageInstallationId: uuidSchema.optional(),
    treatmentInstallationId: uuidSchema.optional(),
    drCode: z
        .string()
        .regex(/^[DR]\d{1,2}$/)
        .optional(),
    treatmentLabel: z.string().trim().max(200).optional(),
    capId: uuidSchema.optional(),
    /** FR-COL-27: the waste goes to another site of the tenant, which confirms the reception. */
    transferSiteId: uuidSchema.optional(),
    otherBsdNumber: z.string().trim().max(80).optional(),
    comment: z.string().trim().max(2000).optional(),
    pickupRequestLineId: uuidSchema.optional(),
});
/** The chargement block (wizard step 2: FR-COL-18). */
export const chargementFieldsSchema = z.object({
    transporterId: uuidSchema.optional(),
    /** FR-PAR-35: the generator carries the waste itself, under its company's authorization. */
    ownTransport: z.boolean().optional(),
    transporterAuthorizationId: uuidSchema.optional(),
    transportMode: z.enum(TRANSPORT_MODES).default('ROUTE'),
    plateNumber: z.string().trim().max(20).optional(),
    driverName: z.string().trim().max(120).optional(),
    /** FR-FIA-17: the language of the written instructions; omitted, the site's document language. */
    driverLanguage: localeSchema.optional(),
    plannedDate: isoDate,
    plannedDateReason: z.string().trim().max(500).optional(),
    responsible: z.string().trim().max(120).optional(),
    contact: z.string().trim().max(120).optional(),
    /** The chargement claims the ADR 1.1.3.6 exemption when it is under the threshold (FR-COL-18). */
    claim1136: z.boolean().default(false),
    comment: z.string().trim().max(2000).optional(),
});
export const chargementCreateSchema = chargementFieldsSchema.extend({
    siteId: uuidSchema,
    expeditions: z.array(expeditionInputSchema).min(1).max(30),
    exemptions: z.array(exemptionSchema.extend({ index: z.number().int().min(0) })).default([]),
    /** Validate, number and plan the expéditions at once instead of keeping a draft (FR-COL-21). */
    submit: z.boolean().default(false),
});
/** Replaces the draft content of a chargement before pickup (FR-COL-24). */
export const chargementUpdateSchema = chargementCreateSchema.omit({ siteId: true }).extend({
    version: z.number().int().min(1),
    expeditions: z
        .array(expeditionInputSchema.extend({ id: uuidSchema.optional() }))
        .min(1)
        .max(30),
});
export const chargementSubmitSchema = z.object({
    version: z.number().int().min(1),
    exemptions: z.array(exemptionSchema.extend({ index: z.number().int().min(0) })).default([]),
});
/** FR-COL-22: pickup confirmation with the actual date and real quantities. */
export const pickupConfirmSchema = z.object({
    actualDate: isoDate,
    /** Checklist items a site manager exempts with a reason (FR-FIA-23). */
    checklistExemptions: z
        .array(z.object({
        item: z.enum(CHECKLIST_ITEMS),
        reason,
    }))
        .default([]),
    plateNumber: z.string().trim().max(20).optional(),
    driverName: z.string().trim().max(120).optional(),
    quantities: z
        .array(z.object({
        expeditionId: uuidSchema,
        quantityT: tonnes,
        estimated: z.boolean().default(false),
    }))
        .default([]),
});
/** FR-COL-27: the receiving site confirms a transfer into its stock, under one of its wastes. */
export const transferConfirmSchema = z.object({
    receivedAt: isoDate,
    quantityReceivedT: tonnes,
    wasteId: uuidSchema,
    originId: uuidSchema.optional(),
    comment: z.string().trim().max(1000).optional(),
});
export const receiveSchema = z.object({
    receivedAt: isoDate,
    quantityReceivedT: tonnes,
    comment: z.string().trim().max(1000).optional(),
});
/**
 * FR-SUI-05: what becomes of refused waste: back in the site's stock, shipped again to another
 * installation on a new BSD, or decided later (a task stays open until then).
 */
export const REFUSAL_FOLLOW_UPS = ['RETURN_TO_STOCK', 'RESHIP', 'LATER'];
export const acceptSchema = z.object({
    acceptedAt: isoDate,
    quantityRefusedT: tonnes.default(0),
    refusalReason: z.string().trim().max(1000).optional(),
    /** For a partial refusal; decided later when absent. */
    followUp: z.enum(REFUSAL_FOLLOW_UPS).optional(),
});
/** The decision taken after a refusal recorded with "decide later" (FR-SUI-05). */
export const refusalFollowUpSchema = z.discriminatedUnion('action', [
    z.object({ action: z.literal('RETURN_TO_STOCK'), date: isoDate }),
    z.object({ action: z.literal('RESHIP'), plannedDate: isoDate }),
]);
/** FR-SUI-03: one reminder e-mail to a prestataire for the BSD it has not returned. */
export const returnReminderSchema = z.object({
    prestataireId: uuidSchema,
    expeditionIds: z.array(uuidSchema).min(1).max(200),
    to: z.array(z.string().trim().toLowerCase().email()).min(1).max(10),
    cc: z.array(z.string().trim().toLowerCase().email()).max(10).default([]),
    message: z.string().trim().max(2000).optional(),
});
/**
 * FR-SUI-06, FR-SUI-07: one line of a bulk return entry or of a prestataire statement: the
 * reception, and the acceptance when the signed copy carries it.
 */
export const returnLineSchema = z.object({
    expeditionId: uuidSchema,
    receivedAt: isoDate,
    quantityReceivedT: tonnes,
    comment: z.string().trim().max(1000).optional(),
    acceptedAt: isoDate.optional(),
    quantityRefusedT: tonnes.optional(),
    refusalReason: z.string().trim().max(1000).optional(),
});
export const returnBatchSchema = z.object({
    lines: z.array(returnLineSchema).min(1).max(200),
});
export const returnListQuerySchema = z.object({
    /** overdue: past the site's return delay; pending: every signed copy still awaited. */
    scope: z.enum(['overdue', 'pending']).default('overdue'),
});
export const refuseSchema = z.object({
    refusedAt: isoDate,
    reason,
    followUp: z.enum(REFUSAL_FOLLOW_UPS).optional(),
    /** Earlier form of the choice: true is RETURN_TO_STOCK, false is LATER. */
    returnedToSite: z.boolean().optional(),
});
export const treatSchema = z.object({
    treatedAt: isoDate,
    drCode: z.string().regex(/^[DR]\d{1,2}$/),
    certificateNumber: z.string().trim().max(80).optional(),
});
export const cancelSchema = z.object({ reason });
/** FR-COL-24: after pickup, a revision with a reason changes descriptive fields and quantities. */
export const reviseSchema = z.object({
    version: z.number().int().min(1),
    reason,
    changes: z
        .object({
        quantityT: tonnes.optional(),
        /** false once the quantity is weighed: the ADR estimated-quantity sentence goes. */
        quantityEstimated: z.boolean().optional(),
        quantityReceivedT: tonnes.optional(),
        otherBsdNumber: z.string().trim().max(80).optional(),
        comment: z.string().trim().max(2000).optional(),
        shippedAt: isoDate.optional(),
        receivedAt: isoDate.optional(),
        treatedAt: isoDate.optional(),
        /** FR-SUI-08: the certificat d'élimination received after the treatment was recorded. */
        certificateNumber: z.string().trim().max(80).optional(),
    })
        .refine((c) => Object.keys(c).length > 0, 'Nothing to revise'),
});
export const expeditionListQuerySchema = z.object({
    status: z.string().optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
    wasteId: uuidSchema.optional(),
    prestataireId: uuidSchema.optional(),
    q: z.string().trim().max(80).optional(),
    chargementId: uuidSchema.optional(),
});
/**
 * Forwarding after temporary storage (Technical Specifications 6.2) and regroupement (FR-COL-28):
 * one child BSD from the storage installation to the final destination, for one or several
 * expéditions of the same waste held in the same installation. BR-23: the child needs the CAP of
 * the final destination, checked when the child is validated.
 */
export const forwardSchema = z.object({
    parentIds: z.array(uuidSchema).min(1).max(50),
    plannedDate: isoDate,
    transporterId: uuidSchema.optional(),
    treatmentInstallationId: uuidSchema,
    drCode: z.string().regex(/^[DR]\d{1,2}$/),
    treatmentLabel: z.string().trim().max(200).optional(),
    capId: uuidSchema.optional(),
    bsdType: z.enum(BSD_TYPES).optional(),
});
/** FR-COL-30: one past expédition, as the history import and the API create it. */
export const HISTORY_STATUSES = ['SHIPPED', 'RECEIVED', 'ACCEPTED', 'TREATED'];
export const expeditionHistorySchema = z
    .object({
    bsdNumber: z.string().trim().min(1).max(80),
    shippedAt: isoDate,
    wasteId: uuidSchema,
    quantityT: tonnes,
    quantityEstimated: z.boolean().default(false),
    conditionnementId: uuidSchema.optional(),
    packages: z.number().int().min(1).max(10_000).optional(),
    originId: uuidSchema.optional(),
    transporterId: uuidSchema.optional(),
    plateNumber: z.string().trim().max(20).optional(),
    treatmentInstallationId: uuidSchema.optional(),
    drCode: z
        .string()
        .regex(/^[DR]\d{1,2}$/)
        .optional(),
    status: z.enum(HISTORY_STATUSES),
    receivedAt: isoDate.optional(),
    quantityReceivedT: tonnes.optional(),
    acceptedAt: isoDate.optional(),
    treatedAt: isoDate.optional(),
    certificateNumber: z.string().trim().max(80).optional(),
    comment: z.string().trim().max(2000).optional(),
})
    .superRefine((v, ctx) => {
    const rank = HISTORY_STATUSES.indexOf(v.status);
    if (rank >= 1 && !v.receivedAt) {
        ctx.addIssue({ code: 'custom', path: ['receivedAt'], message: 'HISTORY: receivedAt' });
    }
    if (rank >= 3 && !v.treatedAt) {
        ctx.addIssue({ code: 'custom', path: ['treatedAt'], message: 'HISTORY: treatedAt' });
    }
    const dates = [v.shippedAt, v.receivedAt, v.acceptedAt, v.treatedAt].filter((d) => Boolean(d));
    for (let i = 1; i < dates.length; i += 1) {
        if (dates[i] < dates[i - 1]) {
            ctx.addIssue({ code: 'custom', path: ['shippedAt'], message: 'HISTORY: dates order' });
            break;
        }
    }
});
//# sourceMappingURL=collecte-schemas.js.map